/**
 * @file CategoryMarkingDialog.tsx
 * @description Маркировка ЧЗ категории (phase08.5 shadcn).
 */

import { useEffect, useState } from "react";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { SelectField } from "../ui/SelectField";
import { Switch } from "../ui/switch";
import { useNotify } from "../ui/NotifyProvider";
import {
  catalogCategories,
  type CatalogCategory,
  type MarkingCategory,
} from "../../lib/api-catalog";
import { ApiError } from "../../lib/api-client";

interface Props {
  open: boolean;
  category: CatalogCategory;
  onClose: () => void;
  onSaved: () => void;
}

/** Товарные группы маркировки — данными (общий `SelectField`). */
const MARKING_OPTIONS = [
  { value: "BAD", label: "БАД (ПП РФ 673)" },
  { value: "PERFUME_COSMETICS", label: "Парфюмерия и косметика (ПП РФ 1957, ТН ВЭД 3304)" },
  { value: "OTHER", label: "Другая группа" },
];

export function CategoryMarkingDialog({ open, category, onClose, onSaved }: Props) {
  const notify = useNotify();
  const [enabled, setEnabled] = useState(category.markingEnabled);
  const [markingCategory, setMarkingCategory] = useState<MarkingCategory>(
    category.markingCategory ?? "PERFUME_COSMETICS",
  );
  const [startDate, setStartDate] = useState(
    category.markingStartDate ? category.markingStartDate.substring(0, 10) : "",
  );
  const [tnvedHint, setTnvedHint] = useState(category.tnvedHint ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEnabled(category.markingEnabled);
    setMarkingCategory(category.markingCategory ?? "PERFUME_COSMETICS");
    setStartDate(
      category.markingStartDate ? category.markingStartDate.substring(0, 10) : "",
    );
    setTnvedHint(category.tnvedHint ?? "");
    setError(null);
  }, [category, open]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (enabled && !markingCategory) {
      setError("Выберите тип маркировки при включении.");
      return;
    }
    setSubmitting(true);
    try {
      await catalogCategories.updateMarking(category.id, {
        markingEnabled: enabled,
        markingCategory: enabled ? markingCategory : undefined,
        markingStartDate: startDate ? new Date(startDate).toISOString() : undefined,
        tnvedHint: tnvedHint.trim() || undefined,
      });
      notify.success("Настройки маркировки сохранены");
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Маркировка «Честный знак» — {category.displayName}
          </DialogTitle>
        </DialogHeader>

        <form
          id="marking-form"
          onSubmit={(e) => void onSubmit(e)}
          className="flex flex-col gap-3 overflow-y-auto"
        >
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <Switch
                checked={enabled}
                onCheckedChange={(c) => setEnabled(c === true)}
                disabled={submitting}
              />
              <span className="text-sm font-medium text-on-surface">
                Категория подлежит маркировке ЧЗ
              </span>
            </label>
            <p className="text-xs text-on-surface-variant mt-1">
              При включении товары категории требуют GTIN и ТН ВЭД на каждом SKU.
            </p>
          </div>

          {enabled ? (
            <>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="m-type">Тип маркировки</Label>
                <SelectField
                  id="m-type"
                  ariaLabel="Товарная группа"
                  value={markingCategory}
                  onChange={(v) => setMarkingCategory(v as MarkingCategory)}
                  options={MARKING_OPTIONS}
                  disabled={submitting}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="m-date">Маркировка обязательна с</Label>
                <Input
                  id="m-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="m-tnved">ТН ВЭД по умолчанию</Label>
                <Input
                  id="m-tnved"
                  value={tnvedHint}
                  onChange={(e) => setTnvedHint(e.target.value)}
                  disabled={submitting}
                />
                <p className="text-xs text-on-surface-variant">
                  Подсказка для SKU. Пример: 3304990000
                </p>
              </div>
            </>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}
        </form>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Отмена
          </Button>
          <Button type="submit" form="marking-form" disabled={submitting}>
            {submitting ? "Сохраняем…" : "Сохранить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
